import kebabCase from 'lodash/kebabCase'
import { ErrorCode } from '~/enums/error-code'
import type { BaseType, ErrorProtected, PasswordProtectedResponse, SingleRecordResponse } from '~/types'

export const usePasswordProtection = <T extends BaseType>(passwordProtectedData: MaybeRef<Maybe<PasswordProtectedResponse<T> | undefined>>, entityName: string = 'page') => {
  const isPasswordRequired = computed<boolean>(() => {
    if (!unref(passwordProtectedData)) {
      return false
    }

    return isIncorrectPassword.value || (unref(passwordProtectedData) as ErrorProtected)?.code === ErrorCode.PasswordProtected
  })

  const isIncorrectPassword = computed<boolean>(() => {
    if (!unref(passwordProtectedData)) {
      return false
    }

    return (unref(passwordProtectedData) as ErrorProtected)?.code === ErrorCode.IncorrectPassword
  })

  const data = computed<Maybe<T>>(() => {
    if (isPasswordRequired.value) {
      return null
    }

    return (unref(passwordProtectedData) as SingleRecordResponse<T>)?.data
  })

  const error = computed<Maybe<ErrorProtected>>(() => {
    if (!isPasswordRequired.value) {
      return null
    }

    return unref(passwordProtectedData) as ErrorProtected
  })

  const message = computed<Maybe<string>>(() => {
    return error.value?.message || null
  })

  const key = computed<string>(() => {
    if (data.value) {
      return `${entityName}-${data.value.id}`
    }

    if (message.value) {
      return kebabCase(message.value)
    }

    return entityName
  })

  return {
    data,
    error,
    isIncorrectPassword,
    isPasswordRequired,
    key,
    message,
  }
}
